import { PREFERENCE_DEFAULT } from "csi/constants";
import { isDefined } from "csi/utility/coreUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, computed, makeObservable, observable, reaction } from "mobx";
import { ChangeEvent, FunctionComponent, SyntheticEvent } from "react";
import { FONT_SCALE_PERCENTAGE_DEFAULT, PrintSize } from "src/c8-platform/type/print";
import CustomViewDialogStore from "../CustomViewDialogStore";
import { ICustomViewUtil } from "../customViewService";
import { ICustomViewDropDownOptions, ICustomViewOptions } from "./CustomViewOptions";
import { some } from "lodash";

/**
 * This store contains information about all common display options
 */
class CustomViewOptionsStore<T extends ICustomViewOptions> extends ReactionStore implements ICustomViewUtil<T> {
	/** the view component for the custom view */
	component: FunctionComponent;

	/** the custom view dialog store */
	readonly customViewDialogStore: CustomViewDialogStore;

	/** all the dropdown boxes selection options for the custom view */
	readonly dropDownOptions: ICustomViewDropDownOptions;

	/** the print font scale validation error indication */
	@observable hasPrintFontScaleValidationError: boolean = false;

	/** all the available options for custom view */
	@observable options: T;

	/** the print font scale validation message */
	@observable printOptionFontScaleValidationMsg: string = "";

	/** the view main section current status (collapse/expand) */
	@observable viewMainOptionsCollapse = false;

	/** the view specific section current status (collapse/expand) */
	@observable viewSpecificOptionsCollapse = false;

	/** the chart section current status (collapse/expand) */
	@observable _chartOptionsCollapse = false;

	/** the print section current status (collapse/expand) */
	@observable _printOptionsCollapse = false;

	constructor(
		dropDownOptions: ICustomViewDropDownOptions,
		component: FunctionComponent,
		customViewDialogStore: CustomViewDialogStore,
		options?: T,
	) {
		super();
		makeObservable(this);
		this.component = component;
		this.dropDownOptions = dropDownOptions;
		this.options = options;
		this.customViewDialogStore = customViewDialogStore;
		this.printOptionFontScaleValidationMsg = this.printFontScaleRangeMsg;

		this.trackReaction(reaction(
			() => this.options.printFontScale,
			() => {
				this.printOptionFontScaleValidationMsg = this.printFontScaleRangeMsg;
				this.hasPrintFontScaleValidationError = false;
			},
		));
	}

	/** indicates whether chart option section is currently collapsed */
	get chartOptionsCollapse(): boolean {
		return this._chartOptionsCollapse;
	}

	/** indicates if there are print options in the store */
	get hasPrintOptions(): boolean {
		return some(this.options, (value, key) => key.startsWith("print"));
	}

	/** print font scale valid range message */
	get printFontScaleRangeMsg(): string {
		return this.customViewDialogStore.loc.resource("ViewPreferences_MinimumAndMaximumValueError", [60, 100]);
	}

	/** default value of the Font Scale field */
	get printOptionFontScaleTextDefaultValue(): string {
		const { printFontScale } = this.options;
		return isDefined(printFontScale) ? printFontScale.toString() : FONT_SCALE_PERCENTAGE_DEFAULT;
	};

	/** indicates whether print option section is currently collapsed */
	get printOptionsCollapse(): boolean {
		return this._printOptionsCollapse;
	}

	@computed get selectedPrintOrientation(): ComponentItem {
		const { printOrientation } = this.options;
		const selectPrintOrientationID = printOrientation || PREFERENCE_DEFAULT;
		return this.dropDownOptions.paperOrientations.find((item) => item.id === selectPrintOrientationID);
	};

	@computed get selectedPrintPaper(): ComponentItem {
		const { printPaper } = this.options;
		const selectPrintPaperID = printPaper || PREFERENCE_DEFAULT;
		return this.dropDownOptions.paperSizes.find((item) => item.id === selectPrintPaperID);
	};

	/** @override */
	@action onChangeOptions = (options: T): void => { 
		this.options = options;
	};

	/** set the value for printAutoScale */
	@action setPrintAutoScale = (event: SyntheticEvent, value: boolean): void => {
		this.options.printAutoScale = value;
	};

	/** set the value for printComments */
	@action setPrintComments = (event: SyntheticEvent, value: boolean): void => {
		this.options.printComments = value;
	};

	/** set the value for printFontScale */
	@action setPrintFontScale = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const value = Number(event.target.value);
		const { loc } = this.customViewDialogStore;

		if (isNaN(value) || value < 0) {
			this.printOptionFontScaleValidationMsg = loc.resource("ValidateNotValid");
			this.hasPrintFontScaleValidationError = true;
		} else if ((value <= 59) || (value > 100)) {
			this.printOptionFontScaleValidationMsg = this.printFontScaleRangeMsg;
			this.hasPrintFontScaleValidationError = true;
		} else {
			this.options.printFontScale = value;
			this.printOptionFontScaleValidationMsg = this.printFontScaleRangeMsg;
			this.hasPrintFontScaleValidationError = false;
		}
	};

	/** set the value for printHeader */
	@action setPrintHeader = (event: SyntheticEvent, value: boolean): void => {
		this.options.printHeader = value;
	};

	@action setPrintOrientation = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.printOrientation = value.id;
	};

	@action setPrintPaper = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.printPaper = value.id as PrintSize;
	};

	/** set the value for showFileSizes */
	@action setShowFileSizes = (event: SyntheticEvent, value: boolean): void => {
		this.options.showFileSizes = value;
	};

	/** toggle the chart option card collapse/expand */
	@action toggleCollapsedChartOptions = (): void => {
		this._chartOptionsCollapse = !this._chartOptionsCollapse;
	};

	/** toggle the print option card collapse/expand */
	@action toggleCollapsedPrintOptions = (): void => {
		this._printOptionsCollapse = !this._printOptionsCollapse;
	};

	/** toggle the view main section collapse/expand */
	@action toggleCollapsedViewMainOptions = (): void => {
		this.viewMainOptionsCollapse = !this.viewMainOptionsCollapse;
	};

	/** toggle the view specific section collapse/expand */
	@action toggleCollapsedViewSpecificOptions = (): void => {
		this.viewSpecificOptionsCollapse = !this.viewSpecificOptionsCollapse;
	};
}

export = CustomViewOptionsStore;
